import React, { useState } from 'react';
import { useNavigation } from 'context/NavigationContext';
import {
    BringzzPageContainer, BringzzButton, BringzzIcon, BringzzText, BringzzPinCode
} from "@bringzz/components";
import useUser from 'hooks/useUser';
import useRequest from 'hooks/useRequest';
import { useLocation } from 'react-router-dom';
import { useAuth } from 'context/AuthContext';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

function useQuery() {
    return new URLSearchParams(useLocation().search);
}

let state = {
    checked: (
        <BringzzIcon
            icon="IconCheck"
            library="Custom"
            className="h-5 w-5 p-1 bg-magic-lilac text-light rounded-full"
            size="12"
        />
    ),
    unChecked: (
        <div></div>
    ),
};

const VerifyMailView = () => {
    const { backPress, navigate } = useNavigation();
    const [isValidInput, setIsValidInput] = useState(false);
    const [validatedToken, setValidatedToken] = useState(null);
    const [resendLoading, setResendLoading] = useState(false);
    const { refreshUserData } = useAuth();
    const { t } = useTranslation();

    // TODO: add params to navigation context + hook to set and fetch
    let query = useQuery();
    let emailValue = query.get('email');
    let userId = query.get('userId');

    const { sendRequest, data, error, loading } = useRequest();

    const { getUser } = useUser();
    const user = getUser();

    const validatePin = async pin => {
        try {
            const response = await sendRequest({
                method: 'POST',
                url: `/module/token/validateEmailChangeToken`,
                data: {
                    token: parseInt(pin)
                }
            });

            if (response.status === 'success') {
                setValidatedToken(pin);
                toast.success('Code verified successfully!');
                return true;
            } else {
                // Show the error message from backend
                toast.error(response.message || 'Invalid verification code');
                return false;
            }
        } catch (error) {
            console.error('Error validating token:', error);
            toast.error('Failed to verify code. Please try again.');
            return false;
        }
    };

    const handleResendCode = async () => {
        if (resendLoading) return;

        setResendLoading(true);
        try {
            const response = await sendRequest({
                method: 'POST',
                url: `/module/token/createEmailChangeToken`,
                data: {
                    newEmail: emailValue,
                    language: 'en'
                }
            });

            if (response.status === 'success') {
                toast.success('Verification code sent successfully!');
            } else {
                toast.error('Failed to resend code. Please try again.');
            }
        } catch (error) {
            console.error('Error resending code:', error);
            toast.error('Failed to resend code. Please try again.');
        } finally {
            setResendLoading(false);
        }
    };

    const handleConfirm = async () => {
        if (!validatedToken) {
            toast.error('Please verify the code first');
            return;
        }

        try {
            const response = await sendRequest({
                method: 'POST',
                url: `/module/profile/changeEmail`,
                data: {
                    validatedToken: validatedToken
                }
            });

            if (response.status === 'success') {
                toast.success('Email changed successfully!');
                await refreshUserData();
                navigate("/settings/account");
            } else {
                toast.error(response.message || 'Failed to change email. Please try again.');
            }
        } catch (error) {
            console.error('Error changing email:', error);
            toast.error('Failed to change email. Please try again.');
        }
    };

    return (
        <BringzzPageContainer
            header={{
                left: <BringzzIcon
                    size="22"
                    icon="IconChevronLeft"
                    folder="LineIcons"
                    className="cursor-pointer"
                    onClick={backPress}
                />,
                title: <BringzzText tag='h2'>{t('component.account.email.verify.title')}</BringzzText>,
            }}
            footer={
                <div className='pb-28 px-6'>
                    <BringzzButton
                        size='lg'
                        className='bg-magic-lilac w-full flex justify-center text-midnight-blue'
                        onClick={handleConfirm}
                        disabled={!isValidInput || loading}
                    >
                        {t('common.save')}
                    </BringzzButton>
                </div>
            }
        >
            <div className='p-6 space-y-4'>
                <div>
                    <BringzzText
                        tag='h4'
                        className='text-midnight-blue/50 font-normal text-center'
                    >
                        {t('component.account.email.verify.description')}
                    </BringzzText>
                    <BringzzText
                        tag='h4'
                        className='text-center'
                    >
                        {emailValue}
                    </BringzzText>
                </div>

                <BringzzPinCode
                    length={4}
                    onChange={pin => console.log('pin change: ', pin)}
                    onValidate={async pin => {
                        return validatePin(pin)
                    }}
                    onSuccess={() => {
                        setIsValidInput(true)
                    }}
                    onError={() => {
                        console.log('Error');
                    }}
                    lowerChildContent={
                        <div className='flex flex-col justify-center align-center mt-6 space-x-2'>
                            <BringzzText
                                tag='h4'
                                className='text-midnight-blue/50 font-normal text-center'
                            >
                                {"Didn't receive code?"} <span className='magic-lilac underline cursor-pointer' onClick={handleResendCode} disabled={resendLoading}>
                                    {resendLoading ? "Sending..." : "Resend code"}
                                </span>
                            </BringzzText>
                        </div>
                    }
                    spacing='space-x-2'
                    disabled={loading}
                    className={loading ? 'opacity-25' : ''}
                ></BringzzPinCode>
            </div>
        </BringzzPageContainer>
    );
}

export default VerifyMailView;
